import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { useAdvisors } from '../../hooks/useAdvisors'
import { DMConversationRow } from './DMConversationRow'

export function DMConversationList({
  conversations, loading, selectedId, onSelect, getOrCreateConversation,
  getStatus, myStatus, setStandby, isDarkMode
}) {
  const { advisor: me } = useCurrentAdvisor()
  const { advisors } = useAdvisors()
  const [showPicker, setShowPicker] = useState(false)
  const [search, setSearch] = useState('')

  const otherAdvisors = (advisors ?? []).filter(a => a.id !== me?.id)
  const filtered = otherAdvisors.filter(a =>
    (a.full_name || a.name || '').toLowerCase().includes(search.toLowerCase())
  )

  const startConversation = async (advisorId) => {
    setShowPicker(false)
    setSearch('')
    const convId = await getOrCreateConversation(advisorId)
    if (convId) onSelect(convId)
  }

  const isOnline = myStatus === 'online'

  return (
    <div className={`w-80 flex-shrink-0 flex flex-col border-r ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
      {/* Header row */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            Messages
          </span>
          {/* Standby toggle */}
          <button
            onClick={() => setStandby(isOnline)}
            title={isOnline ? 'Set to Standby' : 'Set to Online'}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border transition ${
              isOnline
                ? 'bg-green-100 border-green-300 text-green-700'
                : 'bg-yellow-100 border-yellow-300 text-yellow-700'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-500' : 'bg-yellow-500'}`} />
            {isOnline ? 'ONLINE' : 'STANDBY'}
          </button>
        </div>
        <button
          onClick={() => setShowPicker(true)}
          className="p-1.5 rounded-lg hover:bg-sfp-orange/10 text-sfp-orange transition"
          title="New Message"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* New Message Picker */}
      {showPicker && (
        <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-50'}`}>
          <input
            autoFocus
            type="text"
            placeholder="Search advisors…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`w-full text-sm px-3 py-2 rounded-lg border outline-none ${
              isDarkMode
                ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400'
                : 'bg-white border-gray-300 text-sfp-darkgray placeholder-gray-400'
            }`}
          />
          <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
            {filtered.length === 0 && (
              <p className={`text-xs px-2 py-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}>No advisors found</p>
            )}
            {filtered.map(a => (
              <button
                key={a.id}
                onClick={() => startConversation(a.id)}
                className={`w-full text-left text-sm px-2 py-1.5 rounded-lg transition ${
                  isDarkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-gray-100 text-sfp-darkgray'
                }`}
              >
                {a.full_name || a.name}
              </button>
            ))}
          </div>
          <button
            onClick={() => { setShowPicker(false); setSearch('') }}
            className="mt-2 text-xs text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className={`flex items-center justify-center h-32 text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>Loading…</div>
        ) : conversations.length === 0 ? (
          <div className={`p-6 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            No messages yet. Click + to start a conversation.
          </div>
        ) : conversations.map(c => (
          <DMConversationRow
            key={c.id}
            conversation={c}
            isSelected={selectedId === c.id}
            onClick={() => onSelect(c.id)}
            getStatus={getStatus}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>
    </div>
  )
}
